import React from 'react';
import { LayerItem } from './base';
import { AnimatedItem, AnimatedItemImpl, SoundConfig, SoundConstraints } from './animated';
import { VideoMediaImpl } from '../media/VideoMedia';
import { ThemeSystemConfig } from '../../clipCreator/remotion/Components/Theme/Theme';
import { AspectRatio } from '../AspectRatio';
import { TimelineDefaults } from '../TimelineDefaults';

/**
 * Audio configuration for overlay transitions - uses standard SoundConfig
 */
export type OverlayTransitionAudioConfig = SoundConfig;

/**
 * Overlay transition item - video overlays with alpha channel that play between scenes
 * Positioned above scene content but below captions and branding
 * Implements AnimatedItem to integrate with central sound system
 */
export interface OverlayTransitionItem extends AnimatedItem {
    videoSrc: string;                    // Video file path/URL
    overlayDuration: number;             // How long the overlay plays (seconds)
    associatedSceneId: string;           // Which scene this transition belongs to
    
    // Video transparency mode - how to handle transparent video files
    transparencyMode?: 'alpha' | 'screen';
    
    // Optional synchronized audio effect - now uses custom sound effects
    audioEffect?: OverlayTransitionAudioConfig;
}

/**
 * Implementation class for overlay transition items
 * Extends AnimatedItemImpl to integrate with central sound system
 */
export class OverlayTransitionItemImpl extends AnimatedItemImpl implements OverlayTransitionItem {
    videoSrc: string;
    overlayDuration: number;
    associatedSceneId: string;
    transparencyMode?: 'alpha' | 'screen';
    audioEffect?: OverlayTransitionAudioConfig;

    constructor(config: Omit<OverlayTransitionItem, 'render'>) {
        // Initialize AnimatedItem properties
        const animatedConfig = {
            ...config,
            duration: config.overlayDuration, // Duration equals overlay duration
            // Convert audioEffect to customSoundEffects for central sound system
            customSoundEffects: config.audioEffect ? [config.audioEffect] : []
        };
        
        super(animatedConfig);
        
        // Initialize overlay-specific properties
        this.videoSrc = config.videoSrc;
        this.overlayDuration = config.overlayDuration;
        this.associatedSceneId = config.associatedSceneId;
        this.transparencyMode = config.transparencyMode;
        this.audioEffect = config.audioEffect;
    }


    render(fps: number, theme: ThemeSystemConfig, aspectRatio: AspectRatio, defaults: TimelineDefaults, videoConfig?: { width: number; height: number }): React.ReactNode {
        if (!videoConfig) {
            throw new Error('OverlayTransitionItemImpl.render requires videoConfig parameter');
        }

        return (
            <div 
                key={this.id}
                style={{ 
                    position: 'absolute', 
                    width: '100%', 
                    height: '100%',
                    zIndex: 10 // Above scenes and overlays, below text overlays
                }}
            >
                {/* Video overlay with transparency support */}
                {new VideoMediaImpl({
                    id: `${this.id}-video`,
                    type: "video",
                    src: this.videoSrc,
                    role: "main",
                    offset: 0,
                    duration: this.overlayDuration,
                    transparencyMode: this.transparencyMode,
                    effects: [] // No effects needed - the video itself is the effect
                }).render(fps, `${this.id}-video`, this.overlayDuration, 'cover', videoConfig)}

                {/* Audio is now handled centrally by BasicTimeline.renderSoundEffects() */}
                {/* No direct Audio component needed - uses customSoundEffects integration */}
            </div>
        );
    }
}

/**
 * Configuration for adding overlay transitions to scenes
 * This is what users will configure on their scenes
 */
export interface OverlayTransitionConfig {
    videoSrc: string;
    duration: number;
    
    // Video transparency mode - how to handle transparent video files
    transparencyMode?: 'alpha' | 'screen';
    
    // Optional audio effect
    audioEffect?: OverlayTransitionAudioConfig;
}